import React, { useState } from "react";
import GradientButton from "../../components/button/Button";
import "./ComposeMessageModal.css";
import SearchableDropdown from "../../components/searchableDropdown/SearchableDropdown";

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFilter: (filters: {
    name: string;
    category: string;
    lis: string;
    price: string;
    messageType?: string;
    message?: string;
    transmissionMode?: string;
  }) => void;
}

const ComposeMessageModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  onFilter,
}) => {
  const [filters, setFilters] = useState({
    name: "",
    category: "",
    lis: "",
    price: "",
    messageType: "new",
    message: "",
    transmissionMode: "email", // default value
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const handleMessageTypeChange = (type: string) => {
    setFilters((prev) => ({
      ...prev,
      messageType: type,
      message:
        type === "restock"
          ? "Hello, we are excited to let you know that your favorite products have been restocked! Visit our store to place your order before they run out again."
          : "",
    }));
  };

  const handleFilter = () => {
    onFilter(filters);
    onClose();
  };

  const users = [
    { id: "1", full_name: "Jane Doe", email: "jane@example.com", avatar: "" },
    { id: "2", full_name: "John Smith", email: "john@example.com", avatar: "" },
    {
      id: "3",
      full_name: "Alice Johnson",
      email: "alice.johnson@example.com",
      avatar: "",
    },
    {
      id: "4",
      full_name: "Bob Williams",
      email: "bob.williams@example.com",
      avatar: "",
    },
    {
      id: "5",
      full_name: "Emily Brown",
      email: "emily.brown@example.com",
      avatar: "",
    },
    {
      id: "6",
      full_name: "Michael Lee",
      email: "michael.lee@example.com",
      avatar: "",
    },
    {
      id: "7",
      full_name: "Sarah Kim",
      email: "sarah.kim@example.com",
      avatar: "",
    },
    {
      id: "8",
      full_name: "David Clark",
      email: "david.clark@example.com",
      avatar: "",
    },
    {
      id: "9",
      full_name: "Linda Martinez",
      email: "linda.martinez@example.com",
      avatar: "",
    },
    {
      id: "10",
      full_name: "Chris Evans",
      email: "chris.evans@example.com",
      avatar: "",
    },
  ];

  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  if (!isOpen) return null;

  return (
    <div className="orders-modal">
      <div className="orders-modal__overlay" onClick={onClose}></div>
      <div className="orders-modal__content">
        <div className="orders__modal__header">
          <h2 className="orders-modal__title">Compose message</h2>
          <button className="orders-modal__close" onClick={onClose}>
            &times;
          </button>
        </div>
        <form className="orders-modal__form">
          <div className="orders-modal__form-group full">
            <label>Customer</label>
            <button
              type="button"
              className="orders-modal__select-btn"
              onClick={() => setDropdownOpen(true)}
            >
              {selectedUserIds.length === 0
                ? "Select customer(s)"
                : `${selectedUserIds.length} selected`}
            </button>
            {/* Show selected users below button */}
            <div className="orders-modal__selected-users">
              {users
                .filter((u) => selectedUserIds.includes(u.id))
                .map((u) => (
                  <div key={u.id} className="orders-modal__selected-user">
                    <img
                      src={
                        u.avatar ||
                        "https://ui-avatars.com/api/?name=" +
                          encodeURIComponent(u.full_name)
                      }
                      alt={u.full_name}
                      className="orders-modal__selected-avatar"
                    />
                    <div>
                      <div className="orders-modal__selected-name">
                        {u.full_name}
                      </div>
                      <div className="orders-modal__selected-email">
                        {u.email}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="orders-modal__selected-remove"
                      onClick={() =>
                        setSelectedUserIds(
                          selectedUserIds.filter((id) => id !== u.id)
                        )
                      }
                      aria-label="Remove user"
                    >
                      &times;
                    </button>
                  </div>
                ))}
            </div>
          </div>

          {/* --- Mode of Transmission Select --- */}
          <div className="orders-modal__form-group full">
            <label htmlFor="transmissionMode">Mode of transmission</label>
            <select
              id="transmissionMode"
              name="transmissionMode"
              value={filters.transmissionMode || "email"}
              onChange={(e) =>
                setFilters({ ...filters, transmissionMode: e.target.value })
              }
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "5px",
                border: "1px solid #ccc",
                marginTop: "4px",
              }}
            >
              <option value="email">Email</option>
              <option value="phone">Phone Number</option>
            </select>
          </div>

          <SearchableDropdown
            users={users}
            isOpen={isDropdownOpen}
            onClose={() => setDropdownOpen(false)}
            selected={selectedUserIds}
            onSelect={setSelectedUserIds}
          />
        </form>

        <div className="orders-modal__form-group" style={{ width: "100%" }}>
          <label htmlFor="productDesc">Message</label>
          {filters.messageType === "new" ? (
            <textarea
              id="productDesc"
              name="message"
              placeholder="your message....."
              value={filters.message}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: 4,
                resize: "none",
                outline: "none",
                borderRadius: 5,
                border: "1px solid #ccc",
                height: 100,
                fontFamily: "inherit",
              }}
            />
          ) : (
            <div className="orders-modal__restock-template">
              {filters.message}
            </div>
          )}
        </div>

        <div
          className="orders-modal__form-group"
          style={{
            width: "100%",
            display: "flex",
            gap: "1rem",
            marginTop: "1rem",
          }}
        >
          <label
            style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
          >
            <input
              type="checkbox"
              checked={filters.messageType === "new"}
              onChange={() => handleMessageTypeChange("new")}
            />
            New message
          </label>
          <label
            style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
          >
            <input
              type="checkbox"
              checked={filters.messageType === "restock"}
              onChange={() => handleMessageTypeChange("restock")}
            />
            Restock
          </label>
        </div>

        <div className="orders-modal__actions">
          <div className="orders-modal__apply">
            <GradientButton onClick={handleFilter} text="Send" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComposeMessageModal;
